import Vue from 'vue'
import { get } from 'lodash'
import { locale } from '@/plugins'

export default ({ ui, bus, locale, axios, appCache }) => {
  // Require components
  const required = require.context('./Components/Required', true, /\.(vue)$/i)
  required.keys().map(key => ui.component(key.substr(key.lastIndexOf('/') + 1).replace('.vue', ''), required(key).default))

  return new Vue({
    data () {
      return {
        running: [],
        loader: 0,
        axios: null,
        maintenance: false,
        updateIsRequired: false,
        nextMaintenancePing: null
      }
    },
    watch: {
      loader (count) {
        bus.emit('ui/loading', { name: 'api', count })
      }
    },
    methods: {
      setToken (token) {
        if (token === null) {
          delete this.axios.defaults.headers.common['Authorization']
        } else {
          this.axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
        }
      },
      _incrementRunning (method, path, options) {
        this.running.push({ method, path })
        if (options.showLoading) this.loader++
      },
      _decrementRunning (method, path, options) {
        const index = this.running.findIndex(running => running.method.toLowerCase() === method.toLowerCase() && running.path === path)
        this.running.splice(index, 1)
        if (options.showLoading) this.loader--
      },
      _setMaintenanceMode () {
        this.maintenance = true
        const nextMaintenancePing = new Date()
        nextMaintenancePing.setSeconds(nextMaintenancePing.getSeconds() + 10)
        this.nextMaintenancePing = nextMaintenancePing
      },
      _setUpdateRequired () {
        if (this.updateIsRequired === false) {
          this.updateIsRequired = true
          ui.checkForUpdate()
          setTimeout(() => this.updateIsRequired = false, 30000)
        }
      },
      async _request (method, path, params, options = {}) {
        options = Object.assign({
          // Handlers
          showLoading: !options.silent,

          // Params passed to axios
          timeout: 10000,
          onUploadProgress: null,
        }, options)

        this._incrementRunning(method, path, options)
        try {
          const response = await this.axios({
            url: path,
            method,
            params: ['get', 'head', 'options', 'delete'].includes(method) ? params : {},
            data: ['post', 'put', 'patch'].includes(method) ? params : {},
            timeout: options.timeout,
            onUploadProgress: options.onUploadProgress,
            headers: {
              'App-Language': locale.i18n.locale
            }
          })

          const payload = {
            status: response.status
          }

          const responseData = get(response, 'data', {})
          if (typeof responseData === 'object' && responseData !== null) {
            for (const [key, value] of Object.entries(responseData)) {
              payload[key] = value
            }
          }

          this._decrementRunning(method, path, options)

          return payload
        } catch (e) {
          if (e.response) {
            // Handle all common network errors and middleware exceptions
            if (e.response.status === 503) {
              console.log('[API] Application is being updated, please wait.')
              this._setMaintenanceMode()
            } else if (get(e.response, 'data.exception') === 'OutdatedClientException') {
              console.log('[API] Application is outdated, please refresh or update your app.')
              this._setUpdateRequired()
            } else if (get(e.response, 'data.exception') === 'InvalidTokenException') {
              console.log('[API] Token is invalid, disconnecting ...')
              bus.emit('api/disconnected')
            }

            const errors = get(e.response, 'data.errors')
            const message = get(e.response, 'data.message')

            if(errors) {
              let allErrors = []
              for(const error of Object.values(errors)) {
                allErrors = [...allErrors, ...error]
              }
              ui.error(allErrors.join(' '))
            } else if(message) {
              ui.error(message)
            }
          } else if (e.request) {
            ui.ierror('labels.request-error,messages.unreachable-server')
          } else {
            ui.ierror('labels.error,messages.unknown-error-occured')
          }

          this._decrementRunning(method, path, options)
          if (e.response) {
            throw {
              ...e.response,
              exception: get(e.response, 'data.exception'),
              message: get(e.response, 'data.message'),
            }
          }
          else if (e.request) throw e.request
          else throw e
        }
      },
      async get (path, params, options = {}) {
        return await this._request('get', path, params, options)
      },
      async post (path, params, options = {}) {
        return await this._request('post', path, params, options)
      },
      async put (path, params, options = {}) {
        return await this._request('put', path, params, options)
      },
      async delete (path, params, options = {}) {
        return await this._request('delete', path, params, options)
      },
      loading (path, method) {
        if (!path) {
          return this.running.length > 0
        } else {
          return this.running.filter(running => {
            if (method) {
              return running.path === path && running.method.toLowerCase() === method.toLowerCase()
            } else {
              return running.path === path
            }
          }).length > 0
        }
      },
      on (event, callback) {
        return this.$on(event, callback)
      },
      once (event, callback) {
        return this.$once(event, callback)
      },
      off (event, callback) {
        return this.$off(event, callback)
      },
      emit (event, payload) {
        return this.$emit(event, payload)
      },
    },
    created () {
      this.axios = axios.create({
        baseURL: process.env.VUE_APP_API,
        timeout: 10000,
        headers: {
          'App-Version': appCache.version
        }
      })

      // Maintenance check
      setInterval(() => {
        if (this.maintenance) {
          this.axios.get('/ping')
            .then(() => {
              this.maintenance = false
              this.emit('maintenance_finished')
            })
            .finally(() => {
              const nextMaintenancePing = new Date()
              nextMaintenancePing.setSeconds(nextMaintenancePing.getSeconds() + 10)
              this.nextMaintenancePing = nextMaintenancePing
            })
        }
      }, 10 * 1000)
    }
  })
}